import React, { FC, useState } from 'react';
import classNames from 'classnames';
import { useTheme } from '@mui/material/styles';
import { Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ReactMarkdown from 'react-markdown';
import moment from 'moment';
import { toast } from 'react-toastify';

import { ThemeProps } from '@styles/theme';
import { DATE_MMM_DD_YYYY } from '@shared/constants';
import { ToastMessage } from '@shared/components/Toast';
import { ExpandButton } from '@shared/components/Button/ExpandButton';
import { useDownloadablesUIStore } from '@core/useStores';
import { messages } from 'Download/download.messages';
import { DownloadableVersionFileProps } from 'Download/download.types';

import { PlatformBadge } from '../PlatformBadge';
import { useStyles } from './VersionRow.styles';

const formatDate = (value: string | null) =>
    value ? moment(value.replace('T23:59:59Z', 'T12:00:00Z')).format(DATE_MMM_DD_YYYY) : '';

type VersionRowProps = {
    file: DownloadableVersionFileProps,
};

export const VersionRow: FC<VersionRowProps> = ({ file }) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const downloadablesUIStore = useDownloadablesUIStore();
    const [notesExpanded, setNotesExpanded] = useState(false);

    const hasFile = !!file.fileName && file.fileName !== 'empty';
    const hasNotes = !!file.releaseNotes;
    const date = formatDate(file.date);

    const onDownload = () => {
        if (hasFile) {
            const toastId = toast.success(
                <ToastMessage message={'Downloading ' + file.fileName + '...'} type='success' />,
            );
            downloadablesUIStore.downloadDownloadable(file.id, file.fileName, toastId);
        } else {
            toast.error(
                <ToastMessage message={'Missing file'} type='error' />,
                { autoClose: 1000 },
            );
        }
    };

    const toggleNotes = () => hasNotes && setNotesExpanded(prev => !prev);

    return (
        <div className={styles.wrapper}>
            <div className={styles.row}>
                <div
                    className={classNames(styles.header, {
                        [styles.clickable]: hasNotes,
                        [styles.headerExpanded]: hasNotes && notesExpanded,
                    })}
                    onClick={toggleNotes}
                >
                    {file.version && <span className={styles.version}>v{file.version}</span>}
                    <PlatformBadge platform={file.platform} size='small' />
                    {date && <span className={styles.date}>{date}</span>}

                    <span className={styles.spacer} />

                    {hasNotes && (
                        <ExpandButton
                            expand={notesExpanded}
                            className={classNames(styles.chevron, { [styles.chevronExpanded]: notesExpanded })}
                            aria-label={messages['downloadPage.subRow.releaseNotes']}
                            tabIndex={-1}
                        >
                            <ExpandMoreIcon />
                        </ExpandButton>
                    )}
                </div>

                <button
                    className={styles.downloadBtn}
                    onClick={onDownload}
                    disabled={!hasFile}
                    title={messages['downloadPage.download']}
                >
                    <FileDownloadOutlinedIcon className={styles.downloadIcon} />
                    {messages['downloadPage.download']}
                </button>
            </div>

            {hasNotes && (
                <Collapse in={notesExpanded} timeout='auto' unmountOnExit>
                    <div className={styles.notes}>
                        <ReactMarkdown className={styles.markdown}>{file.releaseNotes}</ReactMarkdown>
                    </div>
                </Collapse>
            )}
        </div>
    );
};

VersionRow.displayName = 'VersionRow';
